import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from 'modules/auth/context';
import * as Api from './api';
import type {
  RecommendedTemplatesResponse,
  TemplateDetail,
  TemplateLanguage,
  TemplateListParams,
  TemplateListResponse,
} from './types';

export const TEMPLATES_KEY = ['templates'] as const;

/** Published templates, a page at a time ("Show more"); keeps results on screen while filters change. */
export function useTemplates(params: Omit<TemplateListParams, 'page'>) {
  const { state } = useAuthContext();
  const orgId = state.organization?.id ?? null;
  return useInfiniteQuery<TemplateListResponse>({
    queryKey: [...TEMPLATES_KEY, 'list', orgId, params],
    queryFn: async ({ pageParam }) => (await Api.List({ ...params, page: pageParam as number })).data,
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.limit < last.total ? last.page + 1 : undefined),
    enabled: Boolean(orgId),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Templates ranked for the active org from its onboarding answers. */
export function useRecommendedTemplates(params: { language?: TemplateLanguage; limit?: number }, enabled = true) {
  const { state } = useAuthContext();
  const orgId = state.organization?.id ?? null;
  return useQuery<RecommendedTemplatesResponse>({
    queryKey: [...TEMPLATES_KEY, 'recommended', orgId, params],
    queryFn: async () => (await Api.Recommended(params)).data,
    enabled: enabled && Boolean(orgId),
    staleTime: 5 * 60_000,
  });
}

export function useTemplate(id: string | null) {
  const { state } = useAuthContext();
  const orgId = state.organization?.id ?? null;
  return useQuery<TemplateDetail>({
    // Org templates are org-scoped, so the cache entry is too.
    queryKey: [...TEMPLATES_KEY, 'detail', orgId, id],
    queryFn: async () => (await Api.Get(id as string)).data,
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });
}

/** Copies a template into the org; the new test shows up in the tests list. */
export function useUseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title?: string }) => (await Api.Use(id, { title })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test', 'list'] });
      queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY, 'list'] });
    },
  });
}

/** Saves one of the org's tests as a template only the org can see. */
export function useSaveOrgTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { testId: string; title?: string; description?: string }) => (await Api.SaveOrgTemplate(data)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY, 'list'] }),
  });
}

/** Deletes one of the org's own templates (creator or TESTS_DELETE). */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await Api.DeleteTemplate(id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY] }),
  });
}

/** "Adapt with AI": the template's shape with AI-written questions on the given topic (synchronous; can take ~1 min). */
export function useAdaptTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; topic: string; title?: string; difficulty?: 'easy' | 'medium' | 'hard' }) =>
      (await Api.Adapt(id, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test', 'list'] });
      queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY, 'list'] });
    },
  });
}
